/**
 * Parked-bee pose while the in-box suggestion picker is open.
 * Call only when the picker is open; closed clears this affordance separately.
 */
export const suggestionPickerBeePose = ({
	isLoading,
	isError,
	suggestionsCount,
}: {
	isLoading: boolean;
	isError: boolean;
	suggestionsCount: number;
}): 'searchingLeft' | 'pointingLeft' | 'curious' => {
	if (isLoading) return 'searchingLeft';
	if (!isError && suggestionsCount > 0) return 'pointingLeft';
	return 'curious';
};

/**
 * Same cleaned-length idea as the gratitude gauge: strip +amount / @mentions
 * before deciding the draft is long enough to coach or refine.
 */
export const COMMENT_ASSIST_MIN_DRAFT_LENGTH = 30;

export const commentAssistCoachingPhase = (draftText: string): 'none' | 'short' | 'ready' => {
	const trimmed = draftText.trim();
	if (!trimmed) return 'none';
	const cleaned = trimmed.replace(/\+\d+/, '').replaceAll(/@[^ ]+/g, '').length;
	return cleaned >= COMMENT_ASSIST_MIN_DRAFT_LENGTH ? 'ready' : 'short';
};

export type CommentSuggestionSelection = {
	text: string;
	source: 'bizy_comment_assist' | 'thank_you_chip';
};

/** Minimal editor surface `applyCommentSuggestion` needs. Mirrors the subset of ActionInputHandle it touches. */
export type CommentSuggestionHandle = {
	reset: () => void;
	insertWithHighlight: (content: string) => void;
	focus: () => void;
};

/**
 * Applies a picked comment suggestion to the editor.
 *
 * Refine (Make it shine) returns continuations that complement the user's
 * draft, so they are appended via `insertWithHighlight` (gauge-style) instead
 * of replacing the editor. Ideas picks are cold full-comment replacements, so
 * the editor is reset first. Thank-you chips only show on an empty editor, so
 * they insert without a reset (matching the original behavior).
 */
export const applyCommentSuggestion = (
	handle: CommentSuggestionHandle | null,
	text: string,
	options: { source?: 'bizy_comment_assist' | 'thank_you_chip'; append?: boolean } = {}
) => {
	if (!handle) return;
	if (options.source === 'bizy_comment_assist' && !options.append) {
		handle.reset();
	}
	handle.insertWithHighlight(text);
	handle.focus();
};

/** Analytics fields for a comment submit that may have started from a suggestion chip. */
export const commentSubmitSuggestionProps = (suggestion: CommentSuggestionSelection | null | undefined, submittedText: string) => {
	const usedSuggestion = suggestion != null;
	const suggestionModified = usedSuggestion && submittedText.trim() !== suggestion.text.trim();

	return {
		usedSuggestion,
		suggestionModified,
		...(suggestion ? { suggestionSource: suggestion.source } : {}),
	};
};
